// packages/quiz-engine/src/index.ts
// Pure deterministic Product Finder engine. Zero runtime deps. Shared by the
// embed (packages/embeds) and the on-site section (apps/storefront).

export type QuizAnswers = {
  use: 'personal' | 'shared'
  experience: 'beginner' | 'intermediate' | 'advanced'
  weight: 'under_190' | '190_230' | 'over_230'
  budget: 'under_6k' | '6k_9k' | '9k_plus'
  // Confidence is a refinement; the renderer always collects it, but the engine
  // tolerates its absence so the tree stays unit-testable in isolation.
  confidence?: 'comfortable' | 'some' | 'unsure'
}

// The subset of a product the engine needs to score. Renderers adapt their
// own product shape to this so the engine has zero dependency on display fields.
export type ScorableSet = {
  slug: string
  family: 'stellar' | 'rider' | 'lite' | null
  volume: number | null
  specialOrder: boolean
}

export type Recommendation = {
  heroSlug: string
  runnerUpSlug: string | null
  reason: string
}

// Quiz metadata derived from existing product data so both surfaces can score
// against real fields. Each field prefers an explicit attributes.quiz_* override.
export type QuizMeta = {
  volume: number | null
  riderLevels: string[]
  family: 'stellar' | 'rider' | 'lite' | null
  specialOrder: boolean
}

export type Question = {
  key: keyof QuizAnswers
  title: string
  options: { value: string; label: string }[]
}

// The 5-question flow. Shared so the embed and the on-site section never drift.
export const QUESTIONS: Question[] = [
  {
    key: 'use',
    title: 'Who is this board for?',
    options: [
      { value: 'personal', label: 'Just me' },
      { value: 'shared', label: 'Shared with family & friends of mixed skill' },
    ],
  },
  {
    key: 'experience',
    title: 'How would you rate your experience?',
    options: [
      { value: 'beginner', label: 'Beginner' },
      { value: 'intermediate', label: 'Intermediate' },
      { value: 'advanced', label: 'Advanced' },
    ],
  },
  {
    key: 'confidence',
    title: 'How are you with board sports?',
    options: [
      { value: 'comfortable', label: 'Very comfortable — I surf, skate or wakeboard' },
      { value: 'some', label: 'Some experience' },
      { value: 'unsure', label: 'New to this, a bit unsure of my balance' },
    ],
  },
  {
    key: 'weight',
    title: 'About how much do you weigh?',
    options: [
      { value: 'under_190', label: 'Under 190 lb' },
      { value: '190_230', label: '190 – 230 lb' },
      { value: 'over_230', label: 'Over 230 lb' },
    ],
  },
  {
    key: 'budget',
    title: "What's your budget?",
    options: [
      { value: 'under_6k', label: 'Under $6,000' },
      { value: '6k_9k', label: '$6,000 – $9,000' },
      { value: '9k_plus', label: '$9,000+' },
    ],
  },
]

type Series = 'stellar' | 'rider'
type Size = 'small' | 'medium' | 'large'

function eligible(sets: ScorableSet[], series: Series): ScorableSet[] {
  return sets
    .filter(s => !s.specialOrder && s.family === series && s.volume != null)
    // volume is non-null here (guaranteed by the filter above); ascending = small→large
    .sort((a, b) => (a.volume! - b.volume!))
}

function pickBySize(sorted: ScorableSet[], size: Size): ScorableSet | null {
  if (sorted.length === 0) return null
  if (size === 'small') return sorted[0] // smallest volume
  if (size === 'large') return sorted[sorted.length - 1] // largest volume
  // medium: the middle board for 3+, the larger for 2, the only one for 1.
  if (sorted.length === 1) return sorted[0]
  if (sorted.length === 2) return sorted[1]
  return sorted[Math.floor(sorted.length / 2)]
}

function chooseSeries(a: QuizAnswers): Series {
  if (a.budget === 'under_6k') return 'rider' // budget floor — only Rider is affordable
  return a.use === 'shared' ? 'rider' : 'stellar'
}

function chooseSize(a: QuizAnswers, series: Series): Size {
  if (a.weight === 'over_230') return 'large'
  if (series === 'rider') return 'small'
  // stellar, < 230 lb
  if (a.experience === 'advanced') return 'small'
  if (a.experience === 'beginner') return 'medium'
  // intermediate: upsize on heavier weight band or low confidence
  if (a.weight === '190_230' || a.confidence === 'unsure') return 'medium'
  return 'small'
}

const SIZE_WORD: Record<Size, string> = { small: 'small and lively', medium: 'balanced', large: 'extra stable' }

function reasonFor(a: QuizAnswers, series: Series, size: Size): string {
  if (series === 'rider') {
    return a.use === 'shared'
      ? 'Rugged, easy-to-share board for mixed-skill riders'
      : 'Durable, great-value board that fits your budget'
  }
  const sizeWord = SIZE_WORD[size]
  if (a.experience === 'advanced') return `Carbon performance board, sized ${sizeWord} for an advanced rider`
  if (a.experience === 'beginner') return `Carbon board with ${sizeWord} volume to learn on confidently`
  return `Carbon performance board, ${sizeWord} for your riding`
}

// Runner-up: the natural alternative for the chosen hero.
function runnerUp(
  hero: ScorableSet,
  heroSeries: Series,
  heroSize: Size,
  allSets: ScorableSet[],
): string | null {
  const stellar = eligible(allSets, 'stellar')
  const rider = eligible(allSets, 'rider')
  let candidate: ScorableSet | null = null
  if (heroSeries === 'stellar') {
    if (heroSize === 'small') candidate = pickBySize(stellar, 'medium')
    else if (heroSize === 'medium') candidate = pickBySize(stellar, 'small')
    else candidate = pickBySize(rider, 'large') // large stellar → value alt
  } else {
    if (heroSize === 'large') candidate = pickBySize(stellar, 'large') // large rider → performance alt
    else candidate = pickBySize(rider, 'large')
  }
  if (!candidate || candidate.slug === hero.slug) return null
  return candidate.slug
}

export function recommend(answers: QuizAnswers, sets: ScorableSet[]): Recommendation | null {
  const series = chooseSeries(answers)
  let seriesSets = eligible(sets, series)
  // Fall back to the other series if the chosen one is empty (e.g. catalog gap).
  if (seriesSets.length === 0) {
    const other: Series = series === 'stellar' ? 'rider' : 'stellar'
    seriesSets = eligible(sets, other)
    if (seriesSets.length === 0) return null
    return buildResult(answers, other, seriesSets, sets)
  }
  return buildResult(answers, series, seriesSets, sets)
}

function buildResult(
  answers: QuizAnswers,
  series: Series,
  seriesSets: ScorableSet[],
  allSets: ScorableSet[],
): Recommendation {
  const size = chooseSize(answers, series)
  const hero = pickBySize(seriesSets, size)! // seriesSets non-empty here
  return {
    heroSlug: hero.slug,
    runnerUpSlug: runnerUp(hero, series, size, allSets),
    reason: reasonFor(answers, series, size),
  }
}

// Derived quiz metadata for a product row. Moved out of the embed API's inline
// helper so derivation is shared. Pure — no IO. Uses String.match (non-global
// regex returns the same capture array as a single regex match).
export function deriveQuizMeta(p: {
  name: string
  slug: string | null
  category: string | null
  attributes: unknown
}): QuizMeta | undefined {
  if (p.category !== 'efoil_set') return undefined
  const attrs = (p.attributes ?? {}) as Record<string, unknown>

  const volMatch = p.name.match(/(\d+)\s*L/i)
  const volume = typeof attrs.quiz_volume === 'number'
    ? attrs.quiz_volume
    : volMatch ? Number(volMatch[1]) : null

  const levelStr = String(attrs.rider_level ?? '').toLowerCase()
  const riderLevels = ['beginner', 'intermediate', 'advanced'].filter(l => levelStr.includes(l))

  const slug = (p.slug ?? '').toLowerCase()
  let family: 'stellar' | 'rider' | 'lite' | null = null
  if (typeof attrs.quiz_family === 'string') {
    family = attrs.quiz_family as 'stellar' | 'rider' | 'lite'
  } else if (slug.startsWith('stellar')) family = 'stellar'
  else if (slug.startsWith('rider')) family = 'rider'
  else if (slug.startsWith('lite')) family = 'lite'

  const specialOrder = attrs.special_order === true

  return { volume, riderLevels, family, specialOrder }
}
